import { getDateToString } from '../../../generic-utils/dateUtils/index.esm.js';
import { daysInWeek } from '../intl/index.esm.js';

/**
 * Returns a date object when given a string, "YYYY-MM-DD".
 *
 * new Date("YYYY-MM-DD") and new Date(YYYY,MM,DD) produce different results.
 *
 * When using an ISO string, missing HH:mm:ss are initialized to 0, but the date
 * gets converted to the user's timezone:
 * https://tc39.es/ecma262/multipage/numbers-and-dates.html#sec-date.parse
 *
 * "When the HH, mm, or ss elements are absent, "00" is used.
 * When the sss element is absent, "000" is used.
 * When the UTC offset representation is absent, date-only forms are interpreted as a UTC time and date-time forms
 * are interpreted as a local time."
 *
 * @param {string} dateString - date string in the format "YYYY-MM-DD"
 * @return {Date}  New Date object from provided string.
 */
const getDateFromString = dateString => {
  const dateElems = dateString.split('-');
  return new Date(Number(dateElems[0]), Number(dateElems[1]) - 1, Number(dateElems[2]));
};
/**
 * Returns string representation of a date when given a custom
 * object with year, month, day. That custom object is provided
 * by the calendar-base data.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {object} obj - custom date object
 * @return {string}  string representation of date.
 */
const getDayToString = obj => {
  // eslint-disable-next-line prefer-const
  let {
    year,
    month,
    day
  } = obj;
  month += 1;
  month = month < 10 ? `0${month}` : month;
  day = day < 10 ? `0${day}` : day;
  return `${year}-${month}-${day}`;
};
/**
 * Returns a javascript Date object given a custom Day object provided
 * by the calendar-base data.
 *
 * @param {object} obj - custom date object
 * @return {Date} javascript Date object
 */
const getDayToDate = obj => {
  const {
    year,
    month,
    day
  } = obj;
  return new Date(year, month, day);
};
/**
 * Returns an array of date objects given a start date and a number
 * of nights.
 *
 * @param {string} start - start date in format, "YYYY-MM-DD"
 * @param {number} num - number of nights
 * @return {array}  Array of date objects
 */
const getDateRange = (start, num) => {
  const startDate = getDateFromString(start);
  const dates = [getDateToString(new Date(startDate))];
  for (let i = 0; i < num; i++) {
    dates.push(getDateToString(new Date(startDate.setDate(startDate.getDate() + 1))));
  }
  return dates;
};
/**
 * Returns the number of days between two date strings.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {number}  number of days
 */
const getNumDaysBetweenDateStrings = (startString, endString) => {
  let numDays = 0;
  const startDate = getDateFromString(startString);
  const endDate = getDateFromString(endString);
  // eslint-disable-next-line no-unmodified-loop-condition
  while (endDate > startDate) {
    // eslint-disable-next-line no-plusplus
    numDays++;
    startDate.setDate(startDate.getDate() + 1);
  }
  return numDays;
};
/**
 * Returns whether the date represented by the given custom date object is the last day of the given month.
 *
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the last day of the given month, otherwise false
 */
const isLastDayInMonth = day => {
  const test = getDayToDate(day);
  if (test.getDate() < 28) {
    return false;
  }
  test.setDate(test.getDate() + 1);
  return test.getDate() === 1;
};
/**
 * Returns whether the date represented by the given custom date object is the first day of the given month.
 *
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the first day of the given month, otherwise false
 */
const isFirstDayInMonth = day => {
  const test = getDayToDate(day);
  if (test.getDate() !== 1) {
    return false;
  }
  return true;
};
/**
 * Given a month, add a number of months to it and return the new month.
 *
 * @param year {object} Year of the month
 * @param month {object} month number (zero-based)
 * @param num {object} Number of months to add
 * @returns {object} object with the year and month of the new month
 */
const addMonthsToMonth = (year, month, num) => {
  const date = getDayToDate({
    year,
    month,
    day: '01'
  });
  date.setMonth(date.getMonth() + num);
  return {
    updatedYear: date.getFullYear(),
    updatedMonth: date.getMonth()
  };
};
/**
 * From an array of days, both white spaces(false) or days, it groups them in weeks
 *
 * @param monthDays
 * @returns An array of weeks, each one is an array of days.
 */
const groupMonthDaysInWeeks = monthDays => {
  const weeks = [];
  for (let i = 0; i < monthDays.length; i += daysInWeek) {
    weeks.push(monthDays.slice(i, i + daysInWeek));
  }
  return weeks;
};
var dateUtils = {
  getDateFromString,
  getDayToString,
  getDayToDate,
  getDateRange,
  getNumDaysBetweenDateStrings,
  isFirstDayInMonth,
  isLastDayInMonth,
  addMonthsToMonth,
  groupMonthDaysInWeeks
};
export { dateUtils as default };